import React, { useEffect, useState } from 'react';
import { Droplet, Leaf, Feather, Hand, MapPin } from 'lucide-react';
import { fetchFormulas } from '../../lib/api';
import { getIntent } from '../../lib/ingredientIntent';
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
} from '../ui/hover-card';
import SectionLabel from '../ui-shree/SectionLabel';

/**
 * FormulaLedger — renders the canonical Shreesavaa formula library with
 * full ingredient transparency. Highlights the Pure Kewra Hydrosol base
 * — captured from the sacrificed steam of the distillation.
 */
const FormulaLedger = () => {
  const [formulas, setFormulas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(0);

  useEffect(() => {
    fetchFormulas()
      .then((d) => setFormulas(d.formulas || []))
      .catch(() => setFormulas([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div
        data-testid="formula-ledger-loading"
        className="h-72 bg-[#C1D7E2]/40 animate-pulse"
      />
    );
  }
  if (formulas.length === 0) return null;

  const f = formulas[active];

  return (
    <section
      id="formula-ledger"
      data-testid="formula-ledger"
      className="px-6 md:px-12 py-24 md:py-32 bg-[#F4F8F9] border-t border-[#C1D7E2]"
    >
      <div className="max-w-[1300px] mx-auto">
        <SectionLabel testId="formula-ledger-label">
          The Open Formula Ledger
        </SectionLabel>
        <h3 className="font-serif text-4xl md:text-5xl text-[#7C9CB1] font-light leading-[1.05] mb-4 max-w-3xl">
          Every drop, accounted for. <em className="text-[#90B0BF]">No water.</em>
        </h3>
        <p className="text-[#7C9CB1]/85 font-light leading-relaxed text-lg max-w-2xl mb-12">
          Each Shreesavaa formula is published with full percentages. Our
          aqueous base is never plain water — it is steam-distilled Kewra,
          the soul of every bottle.
        </p>

        {/* Tabs */}
        <div
          data-testid="formula-ledger-tabs"
          className="flex flex-wrap gap-2 border-b border-[#90B0BF]/30 pb-6 mb-12"
        >
          {formulas.map((fm, i) => (
            <button
              key={fm.slug}
              onClick={() => setActive(i)}
              data-testid={`formula-tab-${fm.slug}`}
              className={`px-4 py-2 text-[11px] tracking-[0.22em] uppercase font-medium border transition-colors ${
                i === active
                  ? 'border-[#7C9CB1] bg-[#7C9CB1] text-[#F4F8F9]'
                  : 'border-[#C1D7E2] text-[#7C9CB1] hover:bg-[#C1D7E2]/40'
              }`}
            >
              {fm.name.replace('Shreesavaa ', '')}
            </button>
          ))}
        </div>

        {/* Highlight banner — Pure Kewra Hydrosol Base */}
        <div
          data-testid="formula-highlight"
          className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12 items-stretch"
        >
          <div className="md:col-span-7 bg-gradient-to-br from-[#C1D7E2]/60 to-[#AAC6D4]/40 border border-[#C1D7E2] p-8 md:p-10">
            <div className="flex items-start gap-3 mb-3">
              <Droplet
                size={20}
                strokeWidth={1.2}
                className="text-[#7C9CB1] mt-1"
              />
              <p className="font-mono text-[10px] tracking-[0.32em] uppercase text-[#7C9CB1]">
                {f.highlight}
              </p>
            </div>
            <h4 className="font-serif text-3xl md:text-4xl text-[#7C9CB1] font-light leading-tight">
              {f.name}
            </h4>
            <p className="mt-3 font-serif italic text-[#90B0BF]">{f.tagline}</p>
            <div className="mt-6 flex items-baseline gap-6 font-mono text-xs text-[#7C9CB1]">
              <span>
                Volume ·{' '}
                <span className="text-[#90B0BF]">{f.volume_ml} ml</span>
              </span>
              <span>
                Base ·{' '}
                <span className="text-[#90B0BF]">100% {f.base_liquid}</span>
              </span>
            </div>
          </div>
          <div className="md:col-span-5 bg-[#F4F8F9] border border-[#C1D7E2] p-8 md:p-10 flex flex-col justify-center">
            <Leaf size={20} strokeWidth={1.2} className="text-[#7C9CB1] mb-3" />
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF] mb-2">
              Circular Essence
            </p>
            <p className="font-serif text-xl text-[#7C9CB1] leading-snug">
              Our base is captured from the sacrificed steam of the Kewra
              distillation — a hydrosol, not plain water. We honour the full
              cycle of the plant, ignoring nothing.
            </p>
            <p className="mt-4 font-mono text-[10px] tracking-[0.25em] uppercase text-[#90B0BF]">
              100 % Biodegradable · Prebiotics for the Water
            </p>
          </div>
        </div>

        {/* Ingredient breakdown */}
        {f.display ? (
          <SignatureRecipe display={f.display} />
        ) : (
          <IngredientList ingredients={f.ingredients} />
        )}
      </div>
    </section>
  );
};

const IngredientList = ({ ingredients }) => (
  <div data-testid="formula-ingredients" className="space-y-3">
    {ingredients.map((ing, i) => (
      <IngredientRow key={ing.name + i} ing={ing} index={i} />
    ))}
  </div>
);

const IngredientRow = ({ ing, index }) => {
  const intent = getIntent(ing.name);
  const Row = (
    <div
      data-testid={`formula-ingredient-${index}`}
      className={`grid grid-cols-12 gap-4 items-center border-b border-[#C1D7E2]/60 pb-3 transition-colors ${
        intent ? 'cursor-help hover:bg-[#C1D7E2]/20' : ''
      }`}
    >
      <div className="col-span-12 md:col-span-4">
        <p className="font-serif text-xl text-[#7C9CB1] leading-tight flex items-center gap-2">
          {ing.name}
          {intent && (
            <span
              data-testid={`formula-intent-marker-${index}`}
              className="font-mono text-[9px] tracking-[0.22em] uppercase text-[#90B0BF] border border-[#C1D7E2] px-1.5 py-0.5"
            >
              Intent
            </span>
          )}
        </p>
        <p className="text-xs italic text-[#90B0BF] mt-1">{ing.role}</p>
      </div>
      <div className="col-span-9 md:col-span-7">
        <div className="h-1.5 bg-[#C1D7E2]/50 relative overflow-hidden">
          <div
            className="absolute inset-y-0 left-0 bg-[#7C9CB1]"
            style={{ width: `${Math.min(100, ing.percent)}%` }}
          />
        </div>
      </div>
      <div className="col-span-3 md:col-span-1 text-right">
        <span className="font-mono text-sm text-[#7C9CB1]">{ing.percent}%</span>
      </div>
    </div>
  );

  if (!intent) return Row;

  return (
    <HoverCard openDelay={120} closeDelay={80}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          data-testid={`formula-intent-trigger-${index}`}
          className="block w-full text-left focus:outline-none focus:ring-2 focus:ring-[#7C9CB1]/40"
          aria-label={`Show sourcing intent for ${ing.name}`}
        >
          {Row}
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        side="top"
        align="start"
        sideOffset={12}
        data-testid={`formula-intent-card-${index}`}
        className="w-[360px] border-[#7C9CB1] bg-[#F4F8F9] text-[#7C9CB1] p-0 rounded-none shadow-[0_12px_36px_-8px_rgba(124,156,177,0.35)]"
      >
        <header className="px-5 py-3 bg-[#7C9CB1] text-[#F4F8F9]">
          <p className="font-mono text-[10px] tracking-[0.32em] uppercase text-[#F4F8F9]/80">
            Notes from the Alchemist
          </p>
          <p className="font-serif text-lg leading-tight mt-0.5">{ing.name}</p>
        </header>
        <div className="px-5 py-4 space-y-4">
          <IntentRow icon={Hand} label="Intent" body={intent.intent} />
          <IntentRow icon={Feather} label="Alchemist" body={intent.alchemist} italic />
          <IntentRow icon={MapPin} label="Source" body={intent.source} mono />
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};

const IntentRow = ({ icon: Icon, label, body, italic, mono }) => (
  <div className="flex items-start gap-3">
    <Icon size={14} strokeWidth={1.4} className="text-[#90B0BF] mt-0.5" />
    <div>
      <p className="font-mono text-[9px] tracking-[0.32em] uppercase text-[#90B0BF] mb-1">
        {label}
      </p>
      <p
        className={`text-[13px] leading-relaxed text-[#7C9CB1] ${
          italic ? 'font-serif italic' : mono ? 'font-mono text-[11px]' : 'font-light'
        }`}
      >
        {body}
      </p>
    </div>
  </div>
);

const SignatureRecipe = ({ display }) => (
  <div data-testid="formula-signature-recipe" className="space-y-10">
    <div>
      <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF] mb-3">
        Aqueous base
      </p>
      <div className="flex items-baseline justify-between border-b border-[#C1D7E2]/60 pb-3">
        <p className="font-serif text-2xl text-[#7C9CB1]">{display.base_label}</p>
        <span className="font-mono text-sm text-[#7C9CB1]">
          {display.base_percent}%
        </span>
      </div>
    </div>
    <div>
      <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF] mb-3">
        {display.oil_phase_label}
      </p>
      <IngredientList ingredients={display.oil_phase} />
    </div>
  </div>
);

export default FormulaLedger;
